import React, { useEffect, useMemo } from 'react';
import { Card, Row, Col, Typography, Button, Avatar, Space, Tag, Spin, Result } from 'antd';
import { CheckCircleOutlined, SafetyCertificateOutlined, CloseCircleOutlined, LoadingOutlined, FieldTimeOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import authActions from '../../actions/authActions';

const { Title, Paragraph } = Typography;

const VerifyRegistration: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const verifyState = useSelector((state: any) => state.verifyRegistrationReducer);

  const token = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return q.get('token') || '';
  }, [location.search]);

  useEffect(() => {
    if (token) {
      dispatch(authActions.verifyRegistrationRequest({ token }));
    }
  }, [token, dispatch]);

  const isLoading = verifyState?.isLoading;
  const isSuccess = verifyState?.isSuccess;

  if (!token) {
    return (
      <div className="accept-invite-root">
        <Card className="accept-invite-card">
          <Result
            status="error"
            title="Invalid Link"
            subTitle="No verification token found. Please check the link from your email."
            extra={
              <Button type="primary" onClick={() => navigate('/register')}>
                Go to Register
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="accept-invite-root">
        <Card className="accept-invite-card">
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
            <Title level={4} style={{ marginTop: 24 }}>Verifying your account...</Title>
            <Paragraph type="secondary">Please wait while we activate your account.</Paragraph>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="accept-invite-root">
      <Card className="accept-invite-card">
        <Row gutter={[28, 28]} align="middle" justify="center">
          <Col xs={24} style={{ textAlign: 'center' }}>
            <div className="invite-hero-stack">
              <div className="invite-hero-circle">
                <div className="invite-hero-ring" />
                <div className="invite-hero-inner">
                  <Avatar
                    size={64}
                    icon={
                      isSuccess ? (
                        <CheckCircleOutlined style={{ color: '#52c41a' }} />
                      ) : (
                        <CloseCircleOutlined style={{ color: 'red' }} />
                      )
                    }
                    className="invite-hero-avatar"
                  />
                </div>
              </div>

              <Title level={3} className="invite-title" style={{ marginTop: 24 }}>
                {isSuccess ? 'Account Verified!' : 'Verification Failed'}
              </Title>
              <Space style={{ marginTop: 8, marginBottom: 24 }}>
                {isSuccess ? (
                  <>
                    <Tag color="green" icon={<CheckCircleOutlined />}>Verified</Tag>
                    <Tag color="blue" icon={<SafetyCertificateOutlined />}>Account Active</Tag>
                  </>
                ) : (
                  <>
                    <Tag color="red" icon={<CloseCircleOutlined />}>Failed</Tag>
                    <Tag icon={<FieldTimeOutlined />} color="warning">Expired or Invalid</Tag>
                  </>
                )}
              </Space>

              <div style={{ marginTop: 16 }}>
                {isSuccess ? (
                  <Button type="primary" size="large" onClick={() => navigate('/login')} icon={<CheckCircleOutlined />}>
                    Go to Login
                  </Button>
                ) : (
                  <Button type="primary" size="large" onClick={() => navigate('/register')}>
                    Try Again
                  </Button>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default VerifyRegistration;